import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Settings, Users, Clipboard, CreditCard, Calculator, 
  Package, FlaskConical, Pill, Image, BarChart3,
  ChevronRight
} from 'lucide-react';

const ConfigurationOverview = () => {
  const configGroups = [
    {
      title: 'General setup',
      icon: <Settings className="w-5 h-5" />,
      items: [
        'Clinic setup',
        'Location setup',
        'Practitioner setup',
        'User & role setup',
        'Print setup',
        'Communication setup',
        'Forms setup'
      ]
    },
    {
      title: 'Patient administration',
      icon: <Users className="w-5 h-5" />,
      items: [
        'Registration and check-in/out setup',
        'Patient setup',
        {
          title: 'Appointments',
          subItems: [
            'Appointment type setup',
            'Setup users for physical appointment types',
            'Setup users for virtual appointment types',
            'Work sessions overview',
            'Location calendars',
            'Other appointment setup'
          ]
        }
      ]
    },
    {
      title: 'EHR',
      icon: <Clipboard className="w-5 h-5" />,
      items: ['General', 'Clinical documentation', 'Clinical personnel']
    },
    {
      title: 'Billing & insurance',
      icon: <CreditCard className="w-5 h-5" />,
      items: ['General', 'Invoicing & orders', 'Insurance']
    },
    {
      title: 'Accounting',
      icon: <Calculator className="w-5 h-5" />,
      items: [
        'General',
        'Assets',
        'Bank',
        'Accounts payable',
        'Accounts receivable',
        'General ledger accounting',
        'Period end closing'
      ]
    },
    {
      title: 'Inventory & products',
      icon: <Package className="w-5 h-5" />,
      items: ['General', 'Store management', 'Products & services']
    },
    {
      title: 'Laboratory',
      icon: <FlaskConical className="w-5 h-5" />,
      items: ['General', 'Test setup']
    },
    {
      title: 'Pharmacy',
      icon: <Pill className="w-5 h-5" />,
      items: ['General', 'Medicinal index']
    },
    {
      title: 'Imaging',
      icon: <Image className="w-5 h-5" />,
      items: ['General']
    },
    {
      title: 'Analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      items: ['Analytics overview']
    }
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {configGroups.map((group, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
              <span className="text-gray-400">{group.icon}</span>
              <h2 className="font-bold text-gray-800 text-sm uppercase tracking-tight">{group.title}</h2>
            </div>
            <div className="p-2 flex-grow">
              <ul className="space-y-1">
                {group.items.map((item, i) => {
                  if (typeof item === 'string') {
                    const slug = item.toLowerCase().replace(/\s+/g, '-');
                    return (
                      <li key={i}>
                        <Link 
                          to={`/dashboard/configuration/${group.title.toLowerCase().replace(/\s+/g, '-')}/${slug}`}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 rounded-md transition-colors group"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-indigo-400" />
                          {item}
                        </Link>
                      </li>
                    );
                  } else {
                    return (
                      <li key={i} className="mt-2">
                        <div className="px-3 py-1 flex items-center gap-2">
                          <ChevronRight className="w-3 h-3 text-indigo-500" />
                          <span className="text-sm font-bold text-indigo-600 underline">{item.title}</span>
                        </div>
                        <ul className="ml-6 mt-1 space-y-1">
                          {item.subItems.map((sub, j) => {
                            const slug = sub.toLowerCase().replace(/\s+/g, '-');
                            return (
                              <li key={j}>
                                <Link 
                                  to={`/dashboard/configuration/patient-administration/${slug}`}
                                  className="block px-3 py-1.5 text-[13px] text-gray-500 hover:bg-slate-50 hover:text-indigo-600 rounded-md"
                                >
                                  {sub}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  }
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConfigurationOverview;
